import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import CardInfo from '../../components/CardInfo/CardInfo'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import MainHeading from '../../components/MainHeading/MainHeading'
import PropTypes from 'prop-types'
import RepairIcon from '../../svg/RepairIcon'
import PartsIcon from '../../svg/PartsIcon'
import AppRoutes from '../../routes/appRoutes'
import { parse } from 'qs'
import useScreen from '../../hooks/useScreen'
import TimeLineSteps from '../../components/TimeLineSteps/TimeLineSteps'
import {
    createPartsStepsInitial,
    createServiceStepsInitial,
} from '../../helpers/createRequestutil'
import TermsAgreeModal from './components/Repair/components/TermsAgreeModal'
import Warning from '../../svg/Warning'
import { useNetworkStatus } from '../../utils/networkStatus'
import EstimateIcon from '../../svg/EstimateIcon'
import { useLocation } from 'react-router-dom'

const CardWithIcon = ({
    onClick,
    text,
    image,
    isSelected,
    width,
    height,
    disabled,
}) => {
    const { isMobile, isTablet } = useScreen()
    return (
        <div
            role="button"
            onClick={!disabled ? onClick : undefined}
            className={clsx(
                'flex flex-col items-center justify-center rounded-3xl md:border-2 border gap-2 py-1',
                {
                    'bg-yellow bg-opacity-10 border-yellow': isSelected,
                    'bg-white border-grayLines': !isSelected && !disabled,
                    'bg-grayDisabled bg-opacity-50 cursor-not-allowed border-grayLines': disabled,
                },
                width,
                height,
            )}
        >
            {typeof image === 'string' ? (
                <img
                    src={image}
                    alt="Request Icon"
                    className="object-cover w-12 h-12"
                />
            ) : (
                <div className="w-12 h-12 flex items-center justify-center">
                    {image}
                </div>
            )}
            <p
                className={clsx(
                    'text-textDarkerGray text-base font-normal',
                    disabled && 'opacity-50',
                )}
            >
                {text}
            </p>
            {disabled && (
                <div
                    className={clsx(
                        'w-full h-9 transition-all',
                        isMobile ? 'px-11' : isTablet ? 'px-6' : 'px-12',
                    )}
                >
                    <div className="w-full h-9 bg-white rounded-lg">
                        <div className="w-full h-9 flex flex-row items-center justify-center p-2 gap-1 bg-yellowLight border rounded-lg border-yellowBorder">
                            <Warning />
                            <p className="w-full text-xs font-normal text-textDarkerGray text-center">
                                Unavailable offline
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

CardWithIcon.propTypes = {
    onClick: PropTypes.func,
    text: PropTypes.string,
    isSelected: PropTypes.bool,
    image: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.element,
    ]),
    width: PropTypes.string,
    height: PropTypes.string,
    disabled: PropTypes.bool,
}

const CreateRequestStepper = ({ history }) => {
    const [estimateAgreeModalOpen, setEstimateAgreeModalOpen] = useState(false)
    const [selectedType, setSelectedType] = useState(null)
    const [error, setError] = useState(null)
    const { isMobile } = useScreen()

    const location = useLocation()
    const asset = location.state?.asset

    const isSelected = selected => selectedType === selected

    const equipmentId = parse(window.location.search, {
        ignoreQueryPrefix: true,
    })?.equipmentId

    useEffect(() => {
        if (error != null) {
            setTimeout(() => {
                setError(null)
            }, [5000])
        }
    }, [error])

    const handleRedirectToFlow = typeSelected => {
        setSelectedType(typeSelected)
        switch (typeSelected) {
            case 'estimate':
                if (equipmentId) {
                    history.push(
                        `${AppRoutes.createRepairRequestFlow()}?equipmentId=${equipmentId}&isEstimate=true`,
                    )
                } else {
                    history.push(
                        `${AppRoutes.createRepairRequestFlow()}?isEstimate=true`,
                    )
                }
                break
            case 'service':
                if (equipmentId) {
                    history.push(
                        `${AppRoutes.createRepairRequestFlow()}?equipmentId=${equipmentId}`,
                    )
                } else {
                    history.push(`${AppRoutes.createRepairRequestFlow()}`)
                }
                break
            case 'parts':
                if (equipmentId) {
                    history.push({
                        pathname: AppRoutes.createPartsRequestFlow(),
                        // search: `?equipmentId=${asset?.salesforceID}`, //Leaving it just in case is needed later
                        state: { asset },
                    })
                } else {
                    history.push(`${AppRoutes.createPartsRequestFlow()}`)
                }
                break
            default:
                setError('Please select a type of request')
                break
        }
    }

    const stepsToUse =
        selectedType === 'parts'
            ? createPartsStepsInitial
            : createServiceStepsInitial

    const renderContent = () => {
        const { isOffline } = useNetworkStatus()
        return (
            <div className="w-full h-auto flex md:flex-row flex-col items-center justify-center gap-x-12 gap-y-2">
                <CardWithIcon
                    image={<RepairIcon disabled={isOffline} />}
                    text="Service Repair"
                    onClick={() => handleRedirectToFlow('service')}
                    isSelected={isSelected('service')}
                    height="h-40"
                    width="w-60"
                />
                <CardWithIcon
                    image={<PartsIcon disabled={isOffline} />}
                    text="Parts Quote"
                    onClick={() => handleRedirectToFlow('parts')}
                    isSelected={isSelected('parts')}
                    height="h-40"
                    width="w-60"
                />

                <CardWithIcon
                    image={<EstimateIcon disabled={isOffline} />}
                    text="Service Estimate"
                    onClick={() => {
                        setSelectedType('estimate')
                        setEstimateAgreeModalOpen(true)
                    }}
                    isSelected={isSelected('estimate')}
                    height="h-40"
                    width="w-60"
                    disabled={isOffline}
                />
            </div>
        )
    }

    return (
        <ContentLayout>
            <MainHeading
                isBlack={isMobile}
                title="Create Request"
                content={
                    isMobile ? (
                        <TimeLineSteps
                            isAbsolute={false}
                            isDark
                            showDescription={false}
                            steps={stepsToUse}
                            key="Stepper"
                            isStepper
                        />
                    ) : null
                }
            />
            <div className="md:py-8 md:px-6 px-0 py-0 md:h-auto h-full lg:w-4/5 md:w-11/12 w-full flex items-center justify-center flex-grow">
                <CardInfo
                    hasRounded={!isMobile}
                    title="Type of Request"
                    subTitle="Select the type of request you’d like to create"
                    content={renderContent()}
                    background={isMobile ? 'bg-pageBackground' : null}
                />
            </div>
            <TermsAgreeModal
                isOpen={estimateAgreeModalOpen}
                setIsOpen={isOpen => {
                    setEstimateAgreeModalOpen(isOpen)
                    setSelectedType(isOpen ? 'estimate' : '')
                }}
                onContinue={() => handleRedirectToFlow('estimate')}
            />
            {/* Spacer */}
            <div className="md:flex-grow md:h-full md:block hidden" />
        </ContentLayout>
    )
}

CreateRequestStepper.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }),
}

export default CreateRequestStepper
